import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';
import ImageUpload from '../../../components/ImageUpload';

const HeroesManager = () => {
  const [heroes, setHeroes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingHero, setEditingHero] = useState(null);
  const [formData, setFormData] = useState({
    title: { sv: '', en: '' },
    subtitle: { sv: '', en: '' },
    image_url: '',
    cta_text: { sv: '', en: '' },
    cta_link: '',
    order: 0,
    active: true,
  });

  const loadHeroes = async () => {
    const data = await api.getHeroes();
    setHeroes(data);
  };

  useEffect(() => {
    loadHeroes();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingHero) {
        await api.updateHero(editingHero.id, formData);
        toast.success('Hero uppdaterad!');
      } else {
        await api.createHero(formData);
        toast.success('Hero skapad!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadHeroes();
    } catch (error) {
      toast.error('Ett fel uppstod');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Är du säker?')) {
      await api.deleteHero(id);
      toast.success('Hero raderad!');
      loadHeroes();
    }
  };

  const resetForm = () => {
    setFormData({
      title: { sv: '', en: '' },
      subtitle: { sv: '', en: '' },
      image_url: '',
      cta_text: { sv: '', en: '' },
      cta_link: '',
      order: 0,
      active: true,
    });
    setEditingHero(null);
  };

  const handleEdit = (hero) => {
    setEditingHero(hero);
    setFormData({
      title: hero.title,
      subtitle: hero.subtitle,
      image_url: hero.image_url,
      cta_text: hero.cta_text || { sv: '', en: '' },
      cta_link: hero.cta_link || '',
      order: hero.order,
      active: hero.active,
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="heroes-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Heroes</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-hero-button">
              <Plus className="w-4 h-4 mr-2" />
              Lägg till Hero
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingHero ? 'Redigera Hero' : 'Lägg till Hero'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="hero-form">
              <div>
                <label className="block text-sm font-medium mb-2">Titel (Svenska)</label>
                <Input
                  required
                  value={formData.title.sv}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, sv: e.target.value } })}
                  data-testid="hero-title-sv"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Titel (English)</label>
                <Input
                  value={formData.title.en}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                  data-testid="hero-title-en"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Underrubrik (Svenska)</label>
                <Input
                  required
                  value={formData.subtitle.sv}
                  onChange={(e) => setFormData({ ...formData, subtitle: { ...formData.subtitle, sv: e.target.value } })}
                  data-testid="hero-subtitle-sv"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bild</label>
                <ImageUpload
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  aspectRatio="video"
                  placeholder="Klicka eller dra för att ladda upp hero-bild"
                  data-testid="hero-image-upload"
                />
                <p className="text-xs text-gray-500 mt-1">Eller ange URL manuellt:</p>
                <Input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  className="mt-1"
                  data-testid="hero-image-url"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Ordning</label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  data-testid="hero-order"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="rounded"
                  data-testid="hero-active"
                />
                <label className="text-sm font-medium">Aktiv</label>
              </div>
              <Button type="submit" className="w-full" data-testid="hero-submit">
                {editingHero ? 'Uppdatera' : 'Skapa'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {heroes.map((hero) => (
          <Card key={hero.id} className={!hero.active ? 'opacity-50' : ''}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">{hero.title.sv}</h3>
                  <p className="text-gray-600">{hero.subtitle.sv}</p>
                  <p className="text-sm text-gray-500 mt-2">Ordning: {hero.order}</p>
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(hero)} data-testid={`edit-hero-${hero.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(hero.id)} data-testid={`delete-hero-${hero.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default HeroesManager;
